import { getApps, initializeApp, type FirebaseApp } from "firebase/app";
import { getMessaging, getToken, isSupported as isFcmMessagingSupported } from "firebase/messaging";

function getFirebaseWebConfig(): {
  apiKey: string;
  authDomain?: string;
  projectId: string;
  storageBucket?: string;
  messagingSenderId: string;
  appId: string;
} | null {
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
  const messagingSenderId = import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID;
  const appId = import.meta.env.VITE_FIREBASE_APP_ID;
  if (!apiKey || !projectId || !messagingSenderId || !appId) {
    return null;
  }
  return {
    apiKey,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId,
    appId,
  };
}

function getOrInitApp(): FirebaseApp | null {
  const config = getFirebaseWebConfig();
  if (!config) return null;
  return getApps().length > 0 ? getApps()[0]! : initializeApp(config);
}

export function isFcmWebConfigured(): boolean {
  return getFirebaseWebConfig() != null && Boolean(import.meta.env.VITE_FIREBASE_VAPID_KEY?.trim());
}

/** True si el runtime actual puede usar FCM web (navegador compatible). */
export async function isWebPushRuntimeSupported(): Promise<boolean> {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return false;
  }
  return isFcmMessagingSupported();
}

/**
 * Solicita permiso, registra el service worker de FCM y devuelve el token, o null si no aplica.
 */
export async function obtainWebFcmToken(): Promise<string | null> {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return null;
  }
  if (typeof Notification === "undefined") {
    return null;
  }
  if (!(await isFcmMessagingSupported())) {
    return null;
  }
  const perm = await Notification.requestPermission();
  if (perm !== "granted") {
    return null;
  }
  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY?.trim();
  if (!vapidKey) {
    return null;
  }
  const app = getOrInitApp();
  if (!app) {
    return null;
  }

  const swUrl = import.meta.env.DEV
    ? new URL("../firebase-messaging-sw.ts", import.meta.url)
    : new URL(`${import.meta.env.BASE_URL}firebase-messaging-sw.js`, window.location.origin);

  const registration = await navigator.serviceWorker.register(swUrl, { type: "module", scope: "/" });
  await navigator.serviceWorker.ready;

  const messaging = getMessaging(app);
  return getToken(messaging, {
    vapidKey,
    serviceWorkerRegistration: registration,
  });
}
